import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../Icon/Icon';
import './Header.scss';
import { getPaginatedNotifications } from '../../Reducers/actions/notificationActions';
import useUnreadCountPolling from '../../hooks/useUnreadCountPolling';
import useNotificationPolling from '../../hooks/useNotificationPolling';
import GetAppBanner from '../GetApp/GetAppBanner';
import { useSoftwall } from '../../containers/SoftWall/SoftWall';

const DESKTOP_NAV_ITEMS = [
  { route: '/explore', label: 'Explore', icon: 'explore_inactive' },
  { route: '/news', label: 'News', icon: 'news_nav_v2' },
  { route: '/library', label: 'Library', icon: 'library_v2' },
  { route: '/profile', label: 'Profile', icon: 'person' },
];

const ROOT_TAB_PATHS = [
  '/explore',
  '/news',
  '/search',
  '/search/news',
  '/search/library',
  '/create',
  '/library',
  '/profile',
];

const Header = ({ title, searchType, showSearch = true, banner = false, onCloseBanner, onOpenGetAppModal, onOpenCreateModal, desktopOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');

  const { unreadNotificationsCount, unreadChatsCount } = useSelector((state) => ({
    unreadNotificationsCount: state?.notifications?.unreadNotificationsCount,
    unreadChatsCount: state?.chat?.unread_chats_count
  }));

  // Check authentication status
  const isAuthenticated = useSelector((state) =>
    state.auth?.auth?.isLogged || state.auth?.user || state.auth?.session
  );

  // Define softwall pages
  const SOFTWALL_PAGES = ['/explore', '/news', '/search', '/creation-detail', '/user-profile'];
  const isSoftwallPage = SOFTWALL_PAGES.some(page =>
    location.pathname === page || location.pathname.startsWith(page + '/')
  );

  // Show sign-up button for unauthenticated users on softwall pages
  const showSignUpButton = !isAuthenticated && isSoftwallPage;
  // Show icon buttons for authenticated users
  const showIconButtons = isAuthenticated;

  // Get softwall context to open modal
  const softwall = useSoftwall();

  const handleSignUpClick = () => {
    if (softwall?.openModal) {
      softwall.openModal('signup');
    }
  };

  // Initialize unread count polling for real-time updates
  useUnreadCountPolling();

  // Initialize notification polling for real-time updates
  useNotificationPolling();

  const handleSearchClick = () => {
    // Search is open to logged-out visitors. News and library search need an account,
    // so logged-out visitors land on the public creations/people search instead.
    if (!isAuthenticated) {
      navigate('/search');
      return;
    }
    searchType === "creations" ? navigate('/search') : searchType === "library" ? navigate('/search/library') : navigate('/search/news');
  };

  const handleDesktopSearch = useCallback((e) => {
    e.preventDefault();
    // Carry the query over so /search opens prefilled with results loaded
    const query = searchQuery.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
    setSearchQuery('');
  }, [navigate, searchQuery]);

  const isNavActive = (route) => {
    if (route === '/explore') return location.pathname.startsWith('/explore');
    if (route === '/news') return location.pathname.startsWith('/news') || location.pathname.startsWith('/search/news');
    return location.pathname.startsWith(route);
  };

  const handleNavClick = (item) => {
    if (item.route === '/library' || item.route === '/profile') {
      const sw = softwall;
      if (sw?.requireAuth) {
        const authTitle = item.route === '/library'
          ? 'Sign up or log in to access your library'
          : 'Sign up or log in to access your profile';
        if (!sw.requireAuth(item.label + ' Access', authTitle)) {
          return;
        }
      }
    }
    navigate(item.route);
  };

  useEffect(() => {
    dispatch(getPaginatedNotifications(0, 1, true));
  }, []);

  const isRootTab = ROOT_TAB_PATHS.includes(location.pathname);
  const pageTitleClassName = `page-title${isRootTab ? ' page-title--root-tab' : ''}`;
  const leftSectionClassName = `left-section ml-16${isRootTab ? ' left-section--root-tab' : ''}`;

  return (
    <div className={`d-flex f-column app-header-content${desktopOnly ? ' desktop-only-header' : ''}`}>
      {banner && <GetAppBanner onClose={onCloseBanner} onGetApp={onOpenGetAppModal} />}
      <header className="app-header">
        <div className={leftSectionClassName}>
          <h1 className={pageTitleClassName}>{title}</h1>
          {showSearch && (
            <button className="search-button mobile-only" onClick={handleSearchClick}>
              <Icon alias="search" width={20} />
            </button>
          )}
        </div>

        <nav className="desktop-nav-links">
          {DESKTOP_NAV_ITEMS.map((item) => (
            <button
              key={item.route}
              className={`desktop-nav-item ${isNavActive(item.route) ? 'active' : ''}`}
              onClick={() => handleNavClick(item)}
            >
              {item.label}
            </button>
          ))}
          <button
            className="desktop-nav-item desktop-nav-create"
            onClick={onOpenCreateModal}
          >
            <Icon alias="plusCircle_v2" width={20} height={20} color="#FF3232" />
            <span>Create</span>
          </button>
        </nav>

        {!location.pathname.startsWith('/search') && (
          <div className="desktop-search-bar">
            <form onSubmit={handleDesktopSearch}>
              <Icon alias="search" width={18} />
              <input
                type="text"
                placeholder="Search creations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          </div>
        )}

        <div className="right-section mr-16">
          {showSignUpButton && (
            <button
              className="sign-up-button"
              onClick={handleSignUpClick}
              aria-label="Sign up"
            >
              Sign up
            </button>
          )}
          {showIconButtons && (
            <>
              <span className="header-icon-button notification-button ml-auto" onClick={() => navigate("/notifications")}>
                <Icon alias="notification" width={24} height={24} />
                {unreadNotificationsCount > 0 && (
                  <div className="notification-count">
                  </div>
                )}
              </span>
              <span className="header-icon-button chat-button" onClick={() => navigate('/conversations')}>
                <Icon alias="mail" width={24} />
                {unreadChatsCount > 0 && (
                  <div className="chat-count">
                    <span>{unreadChatsCount}</span>
                  </div>
                )}
              </span>
              <span className="header-icon-button" onClick={() => navigate('/my-profile')}>
                <Icon alias="settings" width={24} fill="none" />
              </span>
            </>
          )}
        </div>
      </header>
    </div>
  );
};

export default Header; 